import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import { Autocomplete, TextField } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { api } from "utils/Api";

function AddSupportPopup({
  open,
  onClose,
  onSubmit,
  organization_id,
  property_id,
  user_id,
  userRole,
  units = [],
}) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    setSelectedUser(null);
  }, [selectedUnit]);

  
  //  Find selected unit object for display
  const selectedUnitObj = units.find((u) => u.id === selectedUnit);
  const unitUsers = selectedUnitObj?.user_list || [];

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;

    const payload = {
      subject,
      description,
      status: "open",
      organization_id,
      property_id,
      unit_id: selectedUnit,
      user_id: userRole === 3 || userRole === 4 ? selectedUser : user_id, // fallback to logged in user
    };

    try {
      setLoading(true);
      await onSubmit(payload);
      setSubject("");
      setDescription("");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  //  Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedUnit(null);
      setSelectedUser(null);
      setSubject("");
      setDescription("");
      setUnitDropdownOpen(false);
    }
  }, [open]);

  //  Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUnitDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* HEADER */}
      <DialogTitle>
        <SoftTypography variant="h6" fontWeight="bold">
          Create Support Query
        </SoftTypography>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent dividers>
        <SoftBox display="flex" flexDirection="column" gap={2} mt={1}>
          {/*  CUSTOM UNIT SELECTOR */}
          <SoftBox>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
              Select Unit <Box component="span" color="error.main">{" "}*</Box>
            </SoftTypography>

            <SoftBox ref={dropdownRef} sx={{ position: "relative" }}>
              {/* Trigger */}
              <SoftBox
                onClick={() => setUnitDropdownOpen((prev) => !prev)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  border: "1px solid",
                  borderColor: unitDropdownOpen ? "#17c1e8" : "#d2d6da",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  minHeight: "40px",
                  transition: "all 0.2s ease",
                  boxShadow: unitDropdownOpen ? "0 0 0 2px rgba(23,193,232,0.15)" : "none",
                  "&:hover": {
                    borderColor: "#17c1e8",
                  },
                }}
              >
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftTypography
                    variant="button"
                    sx={{
                      color: selectedUnitObj ? "#344767" : "#adb5bd",
                      fontWeight: selectedUnitObj ? 500 : 400,
                      fontSize: "0.875rem",
                    }}
                  >
                    {selectedUnitObj ? selectedUnitObj.name : "Choose a unit..."}
                  </SoftTypography>
                </SoftBox>

                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 20,
                    color: "#8392ab",
                    transition: "transform 0.2s ease",
                    transform: unitDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </SoftBox>

              {/* Dropdown List */}
              {unitDropdownOpen && (
                <SoftBox
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    zIndex: 50,
                    maxHeight: "200px",
                    overflowY: "auto",

                    /* Custom scrollbar */
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f8f9fa",
                      borderRadius: "0 8px 8px 0",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#d2d6da",
                      borderRadius: "3px",
                    },
                  }}
                >
                  {units.length === 0 ? (
                    <SoftBox sx={{ p: 2, textAlign: "center" }}>
                      <SoftTypography variant="caption" sx={{ color: "#adb5bd" }}>
                        No units available
                      </SoftTypography>
                    </SoftBox>
                  ) : (
                    units.map((unit) => {
                      const isSelected = selectedUnit === unit.id;

                      return (
                        <SoftBox
                          key={unit.id}
                          onClick={() => {
                            setSelectedUnit(unit.id);
                            setUnitDropdownOpen(false);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 14px",
                            cursor: "pointer",
                            backgroundColor: isSelected ? "#e8f7fc" : "transparent",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              backgroundColor: isSelected ? "#daf1f9" : "#f8f9fa",
                            },
                            "&:first-of-type": {
                              borderRadius: "8px 8px 0 0",
                            },
                            "&:last-of-type": {
                              borderRadius: "0 0 8px 8px",
                            },
                          }}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftTypography
                              variant="button"
                              sx={{
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? "#17c1e8" : "#344767",
                                fontSize: "0.85rem",
                              }}
                            >
                              {unit.name}
                            </SoftTypography>
                          </SoftBox>

                          {isSelected && <CheckIcon sx={{ fontSize: 18, color: "#17c1e8" }} />}
                        </SoftBox>
                      );
                    })
                  )}
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>

          {/* USER SELECTOR - Only for Role 3 & 4 */}
          {selectedUnit && (userRole === 3 || userRole === 4 || userRole === 2) && (
            <SoftBox>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ mb: 0.5, display: "block" }}
              >
                Select User <Box component="span" color="error.main">{" "}*</Box>
              </SoftTypography>

              <Autocomplete
                options={unitUsers}
                getOptionLabel={(option) => option.email || ""}
                value={unitUsers.find((u) => u.id === selectedUser) || null}
                onChange={(event, newValue) => {
                  setSelectedUser(newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search by email..." size="small" />
                )}
                fullWidth
                disableClearable={false}
              />
            </SoftBox>
          )}

          {/* SUBJECT */}
          <SoftBox>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
              Subject <Box component="span" color="error.main">{" "}*</Box>
            </SoftTypography>
            <SoftInput
              placeholder="Enter subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            />
          </SoftBox>

          {/* DESCRIPTION */}
          <SoftBox>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
              Description <Box component="span" color="error.main">{" "}*</Box>
            </SoftTypography>
            <textarea
              placeholder="Describe your issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: "100%",
                minHeight: "120px",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #d2d6da",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#17c1e8";
                e.target.style.boxShadow = "0 0 0 2px rgba(23,193,232,0.15)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d2d6da";
                e.target.style.boxShadow = "none";
              }}
            />
          </SoftBox>
        </SoftBox>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ p: 2 }}>
        <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </SoftButton>

        <SoftButton
          variant="gradient"
          color="info"
          onClick={handleSubmit}
          disabled={loading || !subject.trim() || !description.trim() || !selectedUnit}
        >
          {loading ? "Submitting..." : "Submit"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

AddSupportPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  organization_id: PropTypes.number,
  property_id: PropTypes.number,
  unit_id: PropTypes.number,
  user_id: PropTypes.number,
  units: PropTypes.array,
  userRole: PropTypes.number,
};

export default AddSupportPopup;